import React, { useCallback, useEffect, useRef, useState } from "react";
import * as FaIcons from "react-icons/fa";
import {
  getTopAchieversGuests,
  saveTopAchieversGuests,
  confirmTopAchieversGuests,
} from "../../actions/studensts";
import useTopAchieversLive from "../../utils/useTopAchieversLive";
import AlertSuccess from "../../components/AlertSuccess";
import AlertDenger from "../../components/AlertDenger";
import TablePagination from "../../components/TablePagination";
import { excelTopAchieversGuests } from "../exportToExcel";

// Four guest slots are shown for every student; the "+" adds one more, up to
// nine. These two numbers are mirrored by the server, which caps the stored
// list at MAX_GUESTS.
const DEFAULT_GUESTS = 4;
const MAX_GUESTS = 9;

const EVENT_LOCATIONS = ["Cairo", "Alexandria"];

// Students per page. The server returns only this many at a time (plus the
// header counts), so the page loads quickly however many Top Achievers exist.
const PAGE_SIZE = 100;

const EMPTY_STATS = { total: 0, confirmed: 0, pending: 0, guests: 0 };
const countGuests = (list) => (list || []).filter((n) => n && String(n).trim()).length;

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "confirmed", label: "Confirmed" },
];

const isConfirmed = (s) => Number(s.guests_confirmed) === 1;

// Pads a saved guest list out to the number of visible slots so the row always
// renders at least four inputs.
const toSlots = (guests) => {
  const saved = Array.isArray(guests) ? guests : [];
  const size = Math.max(DEFAULT_GUESTS, saved.length);
  return Array.from({ length: size }, (_, i) => saved[i] || "");
};

function GuestRow({ student, onSaved, onConfirmed, onError }) {
  const [slots, setSlots] = useState(toSlots(student.guests));
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Compared against the saved baseline so the Save button only lights up when
  // there is something to save.
  const [saved, setSaved] = useState(toSlots(student.guests));

  const [eventLocation, setEventLocation] = useState(student.event_location || "");
  const [savedEventLocation, setSavedEventLocation] = useState(student.event_location || "");

  const confirmed = isConfirmed(student);

  const dirty = React.useMemo(
    () =>
      slots.filter((n) => n.trim() !== "").join(" ") !==
      saved.filter((n) => n.trim() !== "").join(" ") ||
      eventLocation !== savedEventLocation,
    [slots, saved, eventLocation, savedEventLocation]
  );

  // Another user's save (live update, or this row remounting from the page's
  // list) replaces what's shown — unless this user has unsaved edits there, in
  // which case their edits stay on screen and the old baseline is kept, so
  // their Save still only applies their own additions/removals.
  const guestsKey = JSON.stringify(student.guests || []);
  useEffect(() => {
    const incoming = toSlots(student.guests);
    const clean = (list) => list.filter((n) => n.trim() !== "").join(" ");
    if (clean(slots) === clean(saved)) {
      setSlots(incoming);
      setSaved(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestsKey]);

  useEffect(() => {
    const incoming = student.event_location || "";
    if (eventLocation === savedEventLocation) {
      setEventLocation(incoming);
      setSavedEventLocation(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student.event_location]);

  const handleChange = (index, value) => {
    setSlots((prev) => prev.map((n, i) => (i === index ? value : n)));
  };

  const handleAdd = () => {
    setSlots((prev) => (prev.length >= MAX_GUESTS ? prev : [...prev, ""]));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    // Send the list this screen started from alongside the edited one, so the
    // server applies only this user's additions/removals and keeps guests
    // another user saved meanwhile. The location is only sent when changed here,
    // so an untouched dropdown can't overwrite someone else's choice.
    const res = await saveTopAchieversGuests(
      student.student_id,
      slots,
      eventLocation !== savedEventLocation ? eventLocation : undefined,
      saved
    );
    setSaving(false);
    if (res && res.status === 200) {
      const stored = toSlots(res.data?.guests);
      setSlots(stored);
      setSaved(stored);
      const storedLocation = res.data?.event_location || "";
      setEventLocation(storedLocation);
      setSavedEventLocation(storedLocation);
      onSaved(student, res.data?.guests || [], res.data?.event_location || null);
    } else {
      onError(res?.data?.ERROR || "Failed to save guests.");
    }
  };

  const handleConfirm = async (value) => {
    if (confirming) return;
    setConfirming(true);
    const res = await confirmTopAchieversGuests(student.student_id, value);
    setConfirming(false);
    if (res && res.status === 200) {
      onConfirmed(student, res.data);
    } else {
      onError(res?.data?.ERROR || "Failed to update confirmation.");
    }
  };

  const filled = slots.filter((n) => n.trim() !== "").length;
  const inputBase =
    "border rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 " +
    "disabled:cursor-not-allowed";

  return (
    <tr
      className={`align-top border-b transition-colors ${
        confirmed ? "bg-green-50 border-green-200" : "bg-white border-gray-100 hover:bg-gray-50"
      }`}
    >
      {/* Student */}
      <td className={`px-4 py-3 border-l-4 ${confirmed ? "border-l-green-500" : "border-l-transparent"}`}>
        <div className="font-semibold text-gray-800">{student.full_name}</div>
        <div className="text-xs text-gray-500 mt-0.5 font-mono">{student.student_id}</div>
        {student.phone && <div className="text-xs text-gray-500">{student.phone}</div>}
      </td>

      {/* Event location */}
      <td className="px-4 py-3 whitespace-nowrap">
        <select
          value={eventLocation}
          onChange={(e) => setEventLocation(e.target.value)}
          disabled={confirmed}
          className={`${inputBase} bg-white border-gray-300 disabled:bg-green-50 disabled:text-gray-600`}
        >
          <option value="">Select Location</option>
          {EVENT_LOCATIONS.map((loc) => (
            <option key={loc} value={loc}>
              {loc}
            </option>
          ))}
        </select>
      </td>

      {/* Guests */}
      <td className="px-4 py-3 min-w-[320px]">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-2">
          {slots.map((name, i) => (
            <input
              key={i}
              type="text"
              value={name}
              placeholder={`Guest ${i + 1}`}
              onChange={(e) => handleChange(i, e.target.value)}
              disabled={confirmed}
              className={`${inputBase} border-gray-300 disabled:bg-green-50 disabled:border-green-200 disabled:text-gray-700`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2 mt-2">
          {!confirmed && (
            <button
              type="button"
              onClick={handleAdd}
              disabled={slots.length >= MAX_GUESTS}
              title={slots.length >= MAX_GUESTS ? `Maximum ${MAX_GUESTS} guests per student` : "Add another guest"}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold text-blue-700
                         bg-blue-50 hover:bg-blue-100 disabled:text-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <FaIcons.FaPlus className="w-2.5 h-2.5" /> Add guest
            </button>
          )}
          <span className="text-xs text-gray-500">
            {filled} of {slots.length} filled · max {MAX_GUESTS}
          </span>
        </div>
      </td>

      {/* Status & actions */}
      <td className="px-4 py-3 w-56">
        {confirmed ? (
          <div className="flex flex-col gap-2">
            <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-green-600 text-white px-2.5 py-1 text-xs font-semibold">
              <FaIcons.FaCheckCircle /> Confirmed
            </span>
            {(student.guests_confirmed_by || student.guests_confirmed_at) && (
              <span className="text-[11px] text-green-800">
                {student.guests_confirmed_by && <>by {student.guests_confirmed_by}</>}
                {student.guests_confirmed_at && <> · {student.guests_confirmed_at}</>}
              </span>
            )}
            <button
              type="button"
              onClick={() => handleConfirm(false)}
              disabled={confirming}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300
                         bg-white text-gray-700 text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              <FaIcons.FaUndo className="w-3 h-3" /> {confirming ? "Updating..." : "Undo"}
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-xs font-semibold">
              <FaIcons.FaHourglassHalf className="w-3 h-3" /> Pending
            </span>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-700 text-white
                         text-xs font-semibold hover:bg-blue-600 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <FaIcons.FaSave className="w-3 h-3" /> {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => handleConfirm(true)}
              disabled={confirming || dirty}
              title={dirty ? "Save your changes before confirming" : "Mark this guest list as done"}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white
                         text-xs font-semibold hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <FaIcons.FaCheckCircle className="w-3 h-3" /> {confirming ? "Confirming..." : "Confirm"}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

// Export options: optional grade range (highest ACT 1 or ACT 2 score).
function ExportModal({ onClose, onExport, exporting }) {
  const [gradeFrom, setGradeFrom] = useState("");
  const [gradeTo, setGradeTo] = useState("");
  const input =
    "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => !exporting && onClose()} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 z-10">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Export Guests Sheet</h2>
            <p className="text-xs text-gray-500 mt-1">
              Exports every Top Achiever and their saved guests. Confirmed students are
              highlighted in green.
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={exporting} className="text-gray-400 hover:text-gray-700">
            <FaIcons.FaTimes />
          </button>
        </div>

        <label className="block text-sm font-semibold text-gray-700 mb-1">
          Grade range <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number" min="1" max="36" placeholder="From"
            value={gradeFrom} onChange={(e) => setGradeFrom(e.target.value)} className={input}
          />
          <span className="text-gray-400">–</span>
          <input
            type="number" min="1" max="36" placeholder="To"
            value={gradeTo} onChange={(e) => setGradeTo(e.target.value)} className={input}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Only students whose highest ACT 1 or ACT 2 score is in this range are exported.
          Leave empty to export all students.
        </p>

        <div className="flex justify-end gap-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={exporting}
            className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onExport({ gradeFrom, gradeTo })}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-700 text-white text-sm font-semibold
                       hover:bg-blue-600 disabled:bg-gray-400"
          >
            <FaIcons.FaFileExcel /> {exporting ? "Exporting..." : "Export Excel"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatTile({ icon: Icon, label, value, tone }) {
  return (
    <div className="rounded-xl bg-white/10 px-4 py-3 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-xl font-bold leading-none">{value}</div>
        <div className="text-xs text-indigo-100 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function TopAchieversGuests() {
  // `students` holds only the current page.
  const [students, setStudents] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // The search actually sent to the server — set by the Search button.
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [showExport, setShowExport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const flashSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };
  const flashError = (msg) => {
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(""), 3500);
  };

  // Rows copy their student's data into local state only when they mount, so
  // the list here must hold what was saved — otherwise searching or paging
  // remounts the row from the stale list and it shows up empty again.
  const handleSaved = (student, guests, event_location) => {
    const before = countGuests(students.find((s) => s.student_id === student.student_id)?.guests);
    const diff = countGuests(guests) - before;
    if (diff) setStats((prev) => ({ ...prev, guests: prev.guests + diff }));
    setStudents((prev) =>
      prev.map((s) =>
        s.student_id === student.student_id ? { ...s, guests, event_location } : s
      )
    );
    flashSuccess(`Guests saved for ${student.full_name}.`);
  };

  const handleConfirmed = (student, data) => {
    const was = isConfirmed(students.find((s) => s.student_id === student.student_id) || {});
    const now = Number(data.guests_confirmed) === 1;
    if (was !== now) {
      const d = now ? 1 : -1;
      setStats((prev) => ({ ...prev, confirmed: prev.confirmed + d, pending: prev.pending - d }));
    }
    setStudents((prev) =>
      prev.map((s) =>
        s.student_id === student.student_id
          ? {
              ...s,
              guests_confirmed: data.guests_confirmed,
              guests_confirmed_by: data.guests_confirmed_by,
              guests_confirmed_at: data.guests_confirmed_at,
            }
          : s
      )
    );
    flashSuccess(
      Number(data.guests_confirmed) === 1
        ? `${student.full_name} confirmed.`
        : `${student.full_name} reopened for editing.`
    );
  };

  // Other users' saves and confirmations, polled from the server; students not
  // in this list (e.g. marked Top Achiever after the page loaded) appear on reload.
  useTopAchieversLive((changes) => {
    const byId = new Map(changes.map((c) => [c.student_id, c]));
    setStudents((prev) =>
      prev.map((s) => {
        const c = byId.get(s.student_id);
        return c
          ? {
              ...s,
              guests: c.guests,
              event_location: c.event_location,
              guests_confirmed: c.guests_confirmed,
              guests_confirmed_by: c.guests_confirmed_by,
              guests_confirmed_at: c.guests_confirmed_at,
            }
          : s;
      })
    );
  });

  // Search only runs when the Search button (or Enter) is pressed, not on
  // every keystroke; clearing the box drops the search straight away.
  const submitSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(search.trim());
  };
  const handleSearchChange = (value) => {
    setSearch(value);
    if (!value.trim()) setAppliedSearch("");
  };

  useEffect(() => {
    setPage(1);
  }, [appliedSearch, statusFilter, locationFilter]);

  // Only the newest request may update the list, so a slow earlier response
  // (e.g. from fast paging) can't overwrite a newer one.
  const requestId = useRef(0);
  const fetchPage = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    const res = await getTopAchieversGuests({
      page,
      pageSize: PAGE_SIZE,
      search: appliedSearch || undefined,
      status: statusFilter === "all" ? undefined : statusFilter,
      location: locationFilter === "all" ? undefined : locationFilter,
    });
    if (id !== requestId.current) return;
    if (res && res.status === 200) {
      setStudents(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setStats(res.data?.stats || EMPTY_STATS);
    } else {
      flashError(res?.data?.ERROR || "Failed to load Top Achievers.");
    }
    setLoading(false);
  }, [page, appliedSearch, statusFilter, locationFilter]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Exports every student and guest (optionally limited to a grade range), not
  // just what is filtered on screen, and re-reads from the server so unsaved
  // edits are never exported.
  const handleExport = async (options) => {
    if (exporting) return;
    setExporting(true);
    try {
      await excelTopAchieversGuests(options);
      setShowExport(false);
    } catch {
      flashError("Failed to export the guests sheet.");
    }
    setExporting(false);
  };

  const progress = stats.total ? Math.round((stats.confirmed / stats.total) * 100) : 0;
  const firstShown = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const lastShown = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="px-4 pb-10">
      {successMsg && <AlertSuccess text={successMsg} />}
      {errorMsg && <AlertDenger text={errorMsg} />}

      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-indigo-800 text-white p-6 shadow-lg mb-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center">
              <FaIcons.FaTrophy className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Top Achievers Guests</h1>
              <p className="text-sm text-indigo-100">
                Students who paid the Top Achievers fee. Record up to {MAX_GUESTS} guests each,
                then confirm when the list is final.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowExport(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-indigo-800 text-sm font-semibold
                       hover:bg-indigo-50 shadow"
          >
            <FaIcons.FaFileExcel /> Export Sheet
          </button>
        </div>

        <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile icon={FaIcons.FaUsers} label="Students" value={stats.total} tone="bg-white/20 text-white" />
          <StatTile icon={FaIcons.FaCheckCircle} label="Confirmed" value={stats.confirmed} tone="bg-green-500 text-white" />
          <StatTile icon={FaIcons.FaHourglassHalf} label="Pending" value={stats.pending} tone="bg-amber-400 text-amber-900" />
          <StatTile icon={FaIcons.FaUserFriends} label="Guests recorded" value={stats.guests} tone="bg-sky-400 text-sky-950" />
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-indigo-100 mb-1">
            <span>Confirmation progress</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 rounded-full bg-white/15 overflow-hidden">
            <div className="h-full bg-green-400 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
        <form onSubmit={submitSearch} className="flex flex-1 min-w-[220px] gap-2">
          <div className="relative flex-1">
            <FaIcons.FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="search"
              placeholder="Search by ID, name or phone"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
          >
            Search
          </button>
        </form>
        <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 text-sm font-medium">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-md transition ${
                statusFilter === f.key ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {f.label}
              <span className="ml-1 text-xs text-gray-400">
                {f.key === "all" ? stats.total : f.key === "confirmed" ? stats.confirmed : stats.pending}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <FaIcons.FaMapMarkerAlt className="text-gray-400" />
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">All locations</option>
            {EVENT_LOCATIONS.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
            <option value="none">Not set</option>
          </select>
        </div>
        <span className="ml-auto text-sm text-gray-500">
          {loading ? (
            "Loading..."
          ) : (
            <>
              Showing <span className="font-semibold text-gray-800">{firstShown}–{lastShown}</span> of{" "}
              <span className="font-semibold text-gray-800">{total}</span>
            </>
          )}
        </span>
      </div>

      {/* Table */}
      {loading && students.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white rounded-xl border">Loading...</div>
      ) : students.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white rounded-xl border">
          {stats.total === 0 ? "No Top Achievers available." : "No students match these filters."}
        </div>
      ) : (
        <div
          className={`overflow-x-auto bg-white border border-gray-200 rounded-xl shadow-sm transition-opacity ${
            loading ? "opacity-60 pointer-events-none" : ""
          }`}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
                <th className="px-4 py-3 font-semibold">Student</th>
                <th className="px-4 py-3 font-semibold">Event Location</th>
                <th className="px-4 py-3 font-semibold">Guests</th>
                <th className="px-4 py-3 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student) => (
                <GuestRow
                  key={student.student_id}
                  student={student}
                  onSaved={handleSaved}
                  onConfirmed={handleConfirmed}
                  onError={flashError}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TablePagination
        page={page}
        setPage={setPage}
        totalItems={total}
        pageSize={PAGE_SIZE}
      />

      {showExport && (
        <ExportModal onClose={() => setShowExport(false)} onExport={handleExport} exporting={exporting} />
      )}
    </div>
  );
}
